import { Store } from '@ngrx/store';
import { AnimatedSprite, Sprite, Spritesheet, Container, ISpritesheetData } from 'pixi.js';
import { ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';

import { createBadgeIcon, createBadgeSprite } from './player.helper';
import { ConnectionStatus, PlayerStateMachine } from './player.state';
import { applyDropShadowFilter } from '../player-ui';

export class NetworkConnectionBadge extends Container {
  private connectedIcon: Sprite | undefined;
  private intermittentIcon: Sprite | undefined;
  private connectingIcon?: AnimatedSprite;
  private stateMachine!: PlayerStateMachine;
  private badge!: Sprite;
  private connectionFeatureFlag = false;
  private marginalConnectionFeatureFlag = false;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    { playerRadius, stateMachine, store }: BadgeProperties,
    sprites: Spritesheet,
    pixiFilters: boolean,
  ) {
    super();
    this.zIndex = 1;
    this.stateMachine = stateMachine;
    this.connectedIcon = createBadgeIcon('badges/connected.svg', sprites);
    this.intermittentIcon = createBadgeIcon('badges/intermittent.svg', sprites);
    if (!this.connectedIcon || !this.intermittentIcon) {
      return;
    }
    this.badge = createBadgeSprite(sprites);
    this.name = 'badge-container';
    applyDropShadowFilter(this.badge, pixiFilters);
    this.addChild(this.badge, this.connectedIcon, this.intermittentIcon);

    void this.createConnectingIcon(sprites, pixiFilters);

    this.position.set(playerRadius - 30, -playerRadius + 30);
    this.stateMachine.stateChange$.pipe(takeUntil(this.destroyed$)).subscribe(this.onPlayerStateChange.bind(this));

    getFeatureFlagObservable(store, LaunchDarklyFlags.social_connection_status_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((connectionFeatureFlag) => (this.connectionFeatureFlag = connectionFeatureFlag));

    getFeatureFlagObservable(store, LaunchDarklyFlags.social_marginal_connection_indicator)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((marginalFeatureFlag) => (this.marginalConnectionFeatureFlag = marginalFeatureFlag));
  }

  private createConnectingIcon(sprites: Spritesheet<ISpritesheetData>, pixiFilters: boolean): void {
    this.connectingIcon = new AnimatedSprite(sprites.animations['badges/connecting']);
    this.connectingIcon.name = 'connecting-icon';
    this.connectingIcon.anchor.set(0.5, 0.5);
    this.connectingIcon.position.set(0, 0);
    this.connectingIcon.animationSpeed = 0.05;
    applyDropShadowFilter(this.connectingIcon, pixiFilters);
    this.addChild(this.connectingIcon);
  }

  private onPlayerStateChange(): void {
    const { connectedBadgeVisible, connectingBadgeVisible, marginalBadgeVisible } = this.stateMachine;

    this.visible =
      ((connectedBadgeVisible || connectingBadgeVisible) && this.connectionFeatureFlag) ||
      (marginalBadgeVisible && this.marginalConnectionFeatureFlag);
    if (!this.connectedIcon || !this.intermittentIcon) {
      return;
    }
    this.connectedIcon.visible = connectedBadgeVisible && this.connectionFeatureFlag;
    this.intermittentIcon.visible = marginalBadgeVisible && this.marginalConnectionFeatureFlag;

    if (connectedBadgeVisible) {
      setTimeout(() => {
        const { state } = this.stateMachine;
        this.stateMachine.setState({ ...state, connectionStatus: ConnectionStatus.NONE });
      }, CONNECTED_BADGE_MS_DISPLAY);
    }

    if (!this.connectingIcon || !this.connectionFeatureFlag) {
      return;
    }
    if (connectingBadgeVisible) {
      this.connectingIcon.visible = true;
      this.connectingIcon.play();
    } else {
      this.connectingIcon.visible = false;
      this.connectingIcon.stop();
    }
  }

  public destroyIndicators(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}

export interface BadgeProperties {
  playerRadius: number;
  stateMachine: PlayerStateMachine;
  store: Store;
}

const CONNECTED_BADGE_MS_DISPLAY = 5000;
